<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <Head :title="t('onboarding.page.title')" />
    <BaseDialog v-model:open="showInsightError" :title="t('onboarding.page.insightErrorTitle')" width="narrow" :reserve-mobile-nav-space="false">
        <BaseNotification theme="error" hide-dismiss :text="t('onboarding.page.insightErrorMessage')" />
        <template #footer
            ><BaseButton theme="primary" @click="showInsightError = false">{{ t("common.dismiss") }}</BaseButton></template
        >
    </BaseDialog>
    <BaseDialog v-model:open="showSkipDialog" :title="t('onboarding.page.skipDialogTitle')" width="narrow">
        <div class="flex flex-col items-center text-center gap-4">
            <div class="w-32 h-32">
                <CoachingModeMarble />
            </div>
            <p class="body-small text-ink-muted">{{ t("onboarding.page.skipDialogNotTailored") }}</p>
            <p class="body-small text-ink-muted">{{ t("onboarding.page.skipDialogComeBack") }}</p>
        </div>
        <template #footer>
            <BaseButton data-testid="skip-personalization-dialog" theme="tertiary" @click="handleSkip">{{ t("onboarding.page.skipButton") }}</BaseButton>
            <BaseButton theme="primary" @click="showSkipDialog = false">{{ t("onboarding.page.continueButton") }}</BaseButton>
        </template>
    </BaseDialog>
    <Transition name="fade" mode="out-in" appear>
        <div
            class="full-height-dynamic safe-area-top-min relative px-3 md:px-6 pt-3 md:pt-6 pb-3 md:pb-6 flex flex-col items-center md:items-stretch gap-10 h-screen w-full overflow-y-auto">
            <div v-if="shouldShowProgressBar" class="top-row place-items-center md:px-4">
                <Transition name="fade">
                    <ProgressBar
                        class="top-row-progress"
                        :current-step="step"
                        :question-index="currentQuestionIndex"
                        :steps="STEP"
                        :is-answered="questionsComponentRef?.isAnswered" />
                </Transition>
                <Transition name="fade">
                    <div v-if="showSkip" class="top-row-skip place-self-end">
                        <BaseButton theme="tertiary" type="button" :disabled="isLoading || isRecordingProcessing" data-testid="skip-personalization" @click="showSkipDialog = true">
                            {{ t("onboarding.page.skipButton") }} <i class="bi bi-chevron-right icon-bold" aria-hidden="true" />
                        </BaseButton>
                    </div>
                </Transition>
            </div>

            <Bubbles direction="bottom" :is-footer="true" />
            <div class="md:order-1 w-full flex flex-col items-center justify-end md:gap-4 flex-1">
                <!-- Main content -->
                <div class="w-full flex flex-col justify-center items-center gap-4 flex-1">
                    <div v-if="teamCompletionFailed" class="flex w-full max-w-lg flex-col gap-3">
                        <BaseNotification theme="error" hide-dismiss :text="t('teamJoin.failed')" />
                        <BaseButton theme="secondary" :disabled="isLoading" @click="completeOnboarding(teamCompletionIntent)">{{ t("teamJoin.retry") }}</BaseButton>
                    </div>
                    <!-- Small marble - hide during insights loading; also hide on mobile path selection to preserve vertical space above the fold -->
                    <div v-if="!(step === STEP.INSIGHTS && insights.length === 0)" :class="{ 'max-md:hidden': step === STEP.PATH_SELECTION }" class="w-16 h-16">
                        <CoachingModeMarble />
                    </div>
                    <Personalization
                        v-if="step === STEP.PERSONALIZATION"
                        :errors="errors?.personalization"
                        :hris-data="hris_data"
                        :user-prefill="user_prefill"
                        @submit="onPersonalizationSubmit" />

                    <OnboardingGreeting
                        v-else-if="step === STEP.GREETING"
                        :busy="isLoading"
                        :greeting-flow="greeting_flow"
                        :first-name="user_details.first_name"
                        :role="role"
                        :organization-name="organizationDetails.is_default ? '' : organizationDetails.name"
                        @get-started="getStarted" />
                    <OnboardingPathSelection v-else-if="step === STEP.PATH_SELECTION" @path-selected="handlePathSelection" />
                    <Questions
                        v-else-if="step === STEP.QUESTIONS"
                        ref="questionsComponentRef"
                        :disabled="isLoading"
                        :is-loading="isLoading"
                        :error="error"
                        :current-question="currentQuestion"
                        :better-voice-composer-enabled="better_voice_composer_enabled"
                        @answer="handleQuestionAnswer"
                        @next="handleNextQuestion"
                        @dismiss-error="error = null"
                        @transcribe-error="handleErrorFallback" />
                    <!-- Loading state for insights generation -->
                    <div v-else-if="step === STEP.INSIGHTS && insights.length === 0" class="flex flex-col gap-10 w-full items-center justify-center">
                        <div class="w-32 h-32">
                            <CoachingModeMarble :is-loading="true" size="big" />
                        </div>
                        <Transition name="message-fade" mode="out-in">
                            <div :key="currentLoadingMessage" class="text-center">
                                <p class="text-ink-muted title-body tracking-[-0.64px]">{{ currentLoadingMessage }}</p>
                            </div>
                        </Transition>
                    </div>
                    <!-- Insights component when loaded -->
                    <Insights
                        v-else-if="step === STEP.INSIGHTS && insights.length > 0"
                        :insights="insights"
                        :team-invitation="hasTeamInvitation"
                        :team-action-disabled="isLoading"
                        @done="goToTeam"
                        @chat-selected="setTeamJoinDestination('current')"
                        @checkin="handleCheckin" />
                    <ScheduleCheckin
                        v-else-if="step === STEP.CHECKIN"
                        :redirect-path="post_onboarding_redirect"
                        :team-invitation="hasTeamInvitation && teamJoinDestination() === 'team'"
                        :title="insight_checkin.title || t('onboarding.page.checkinDefaultTitle')"
                        :description="insight_checkin.content || ''" />
                </div>
                <!-- Start of perceived footer -->
                <div class="flex items-center justify-between mt-10 md:my-6 w-full">
                    <div v-if="showPrivacyDialog" :class="isRecordingProcessing || isLoading ? 'hidden md:block' : ''">
                        <PrivacyDialog :disabled="isRecordingProcessing || isLoading" />
                    </div>
                    <img :src="ColorLogo" :alt="t('onboarding.page.logoAlt')" class="h-5 w-auto" />
                    <div class="flex flex-col gap-5 items-center">
                        <div class="language-selector flex items-center justify-center">
                            <BaseSelect
                                v-model="userLanguage"
                                :aria-label="t('onboarding.page.languageSelectLabel')"
                                :options="languageOptions"
                                :trigger-class="highlightLanguage ? 'language-highlight' : undefined"
                                @update:model-value="handleLanguagePreference" />
                        </div>
                        <div v-if="errors?.language_preference?.default_language" role="alert" class="body-small text-chip-status-overdue-text">
                            {{ errors.language_preference.default_language }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
    <CookieBanner />
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { Head, router } from "@inertiajs/vue3";
import ColorLogo from "~img/color-logo.svg";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import Bubbles from "~vue/components/Bubbles.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { useStatusBar } from "~vue/composables/useStatusBar";
import Insights from "~vue/Onboarding/Insights.vue";
import OnboardingGreeting from "~vue/Onboarding/OnboardingGreeting.vue";
import OnboardingPathSelection from "~vue/Onboarding/OnboardingPathSelection.vue";
import Questions from "~vue/Onboarding/OnboardingQuestions.vue";
import Personalization from "~vue/Onboarding/Personalization.vue";
import PrivacyDialog from "~vue/Onboarding/PrivacyDialog.vue";
import ProgressBar from "~vue/Onboarding/ProgressBar.vue";
import ScheduleCheckin from "~vue/Onboarding/ScheduleCheckin.vue";
import { createTeamInviteOnboarding } from "~vue/Teams/completeTeamInviteOnboarding";
import { pendingTeamJoin, setTeamJoinDestination, teamJoinDestination } from "~vue/Teams/teamJoinContinuation";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

useStatusBar();
const { t } = useI18n();
const hasTeamInvitation = pendingTeamJoin() !== null;

const STEP = {
    PERSONALIZATION: "personalization",
    GREETING: "greeting",
    PATH_SELECTION: "path_selection",
    QUESTIONS: "questions",
    INSIGHTS: "insights",
    OUTRO: "outro",
    CHECKIN: "checkin",
};

const FADE_DELAY_MS = 500;

const { $setEventContext } = inject("globalProperties");
const props = defineProps({
    errors: Object,
    language_options: Array,
    default_language: String,
    insights: Array,
    step: String,
    event_context: Object,
    user_details: Object,
    post_onboarding_redirect: String,
    role: String,
    insight_checkin: {
        type: { title: String, content: String },
        default: () => ({ title: "", content: "" }),
    },
    script_code: String,
    greeting_flow: Array, // Array of {title, subtitle} objects for multi-page greeting
    hris_data: Object, // HRIS employee data from the HRIS sync
    user_prefill: Object, // Fallback name/job_title from the user record and SSO/SCIM ingest, used where hris_data has nothing
    better_voice_composer_enabled: { type: Boolean, default: false },
    skip_echo: { type: Boolean, default: false },
});

const teamInviteOnboarding = createTeamInviteOnboarding(() => props.user_details);
const teamCompletionFailed = ref(false);
let teamCompletionIntent = "done";
const error = ref(null);
const showInsightError = ref(false);
const isLoading = ref(false);
const questionsComponentRef = useTemplateRef("questionsComponentRef");
const userLanguage = ref(props.default_language);
const languageOptions = computed(() => (props.language_options ?? []).map(([language]) => ({ key: language, label: language })));
const insights = ref(props.insights || []);
const highlightLanguage = ref(false);

const organizationDetails = inject("organization_details");

// Loading messages that cycle while generating insights
const loadingMessages = computed(() => [
    t("onboarding.page.loadingConsidering"),
    t("onboarding.page.loadingReflecting"),
    t("onboarding.page.loadingPreparing"),
    t("onboarding.page.loadingThinking"),
]);
const currentLoadingMessageIndex = ref(0);
const currentLoadingMessage = computed(() => loadingMessages.value[currentLoadingMessageIndex.value]);
let messageInterval = null;

// Getters so the copy follows a locale switch while the per-question answer state stays mutable.
const questions = ref([
    {
        get title() {
            return t("onboarding.page.workWorldQuestion.title");
        },
        get text() {
            return t("onboarding.page.workWorldQuestion.text");
        },
        answer: null,
        llmResponse: null,
        offTopic: false,
    },
    {
        get title() {
            return t("onboarding.page.prioritiesQuestion.title");
        },
        get text() {
            return t("onboarding.page.prioritiesQuestion.text");
        },
        answer: null,
        llmResponse: null,
        offTopic: false,
    },
]);

const currentQuestionIndex = ref(0);
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);
const showSkip = computed(() => props.step === STEP.QUESTIONS);
const showPrivacyDialog = computed(() => {
    // PrivacyDialog is more integrated in the personalization and outro step, so it's added there.
    return [STEP.PERSONALIZATION, STEP.QUESTIONS, STEP.INSIGHTS, STEP.CHECKIN, STEP.GREETING, STEP.PATH_SELECTION].includes(props.step);
});

// Show progress bar in all steps except when multipage greeting is enabled
// When greeting_flow is empty/null (feature disabled), show progress bar in all steps including GREETING and PATH_SELECTION
const shouldShowProgressBar = computed(() => {
    // Hide during GREETING and PATH_SELECTION steps only if multipage greeting is enabled (greeting_flow has content)
    const isMultipageGreetingEnabled = props.greeting_flow && props.greeting_flow.length > 0;

    if (isMultipageGreetingEnabled && (props.step === STEP.GREETING || props.step === STEP.PATH_SELECTION)) {
        return false;
    }

    // Show for all other cases
    return true;
});

const isRecordingProcessing = computed(() => questionsComponentRef.isProcessing);
const isLastQuestion = computed(() => currentQuestionIndex.value === questions.value.length - 1);

const showSkipDialog = ref(false);

const insightsRequestBody = ref({});

const { data: insightsData, error: insightsFetchError, execute: fetchInsights } = useFetch("/v1/onboarding/insights", { immediate: false }).post(insightsRequestBody).json();

// Watch for insights step and auto-fetch insights if empty
watch(
    () => props.step,
    async (newStep) => {
        if (newStep === STEP.INSIGHTS && insights.value.length === 0) {
            isLoading.value = true;

            // Start cycling through loading messages
            currentLoadingMessageIndex.value = 0;
            messageInterval = setInterval(() => {
                currentLoadingMessageIndex.value = (currentLoadingMessageIndex.value + 1) % loadingMessages.value.length;
            }, 3000); // Change message every 3 seconds

            try {
                await fetchInsights();

                if (insightsFetchError.value) {
                    throw new Error(insightsFetchError.value.message || insightsFetchError.value);
                }

                if (insightsData.value?.error) {
                    throw new Error(insightsData.value.error);
                }

                if (insightsData.value?.insights) {
                    insights.value = insightsData.value.insights;
                } else {
                    throw new Error("Unexpected response structure");
                }
            } catch (e) {
                logError(e);
                showInsightError.value = true;
            } finally {
                isLoading.value = false;
                // Stop cycling messages when done
                if (messageInterval) {
                    clearInterval(messageInterval);
                    messageInterval = null;
                }
            }
        }
    },
    { immediate: true }, // Run immediately in case we're already on insights step
);

onMounted(() => {
    $setEventContext(props.event_context);

    // Briefly highlight the language dropdown to draw attention to it
    highlightLanguage.value = true;
    setTimeout(() => (highlightLanguage.value = false), 3000);
});

onUnmounted(() => {
    $setEventContext({});
    // Clean up message interval
    if (messageInterval) {
        clearInterval(messageInterval);
        messageInterval = null;
    }
});

function handleNextQuestion() {
    if (isLastQuestion.value) {
        isLoading.value = true;
        questions.value[currentQuestionIndex.value].showMemoryAnimation = true;
        /*
         * Allow the saved memory animation to run completely
         * Submit via Inertia to save questions and navigate to insights step
         */
        postResponse(
            {
                intent: "insights",
                form: questions.value.map((q) => ({ question: q.text, answer: q.answer })),
            },
            {
                only: ["step", "insights", "errors"],
                onFinish: () => {
                    isLoading.value = false;
                },
            },
        );
    } else {
        nextTick(() => {
            questions.value[currentQuestionIndex.value].showMemoryAnimation = true;
            currentQuestionIndex.value++;
        });
    }
}

async function handleQuestionAnswer({ answer, append, onFinish, usingMic }) {
    let nextAnswer;
    isLoading.value = true;
    error.value = null;

    const currentAnswer = questions.value[currentQuestionIndex.value].answer;
    if (append && currentAnswer !== null) {
        nextAnswer = `${currentAnswer}\n\n${answer}`;
    } else {
        logUserInteraction(`onboarding_q${currentQuestionIndex.value + 1}_completed`, { mic: usingMic, skip_echo: props.skip_echo });
        nextAnswer = answer;
    }

    if (props.skip_echo) {
        questions.value[currentQuestionIndex.value].answer = nextAnswer;
        questions.value[currentQuestionIndex.value].offTopic = false;
        isLoading.value = false;
        onFinish();
        handleNextQuestion();
        return;
    }

    try {
        const response = await fetch("/coach/active_listening", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                form: {
                    question: questions.value[currentQuestionIndex.value].text,
                    answer: nextAnswer,
                },
            }),
        });

        if (!response.ok) {
            throw new Error("Failed to obtain active_listening response");
        }

        const payload = await response.json();

        if (payload.status === "guardrails_violated") {
            questions.value[currentQuestionIndex.value].offTopic = true;
            return;
        }

        questions.value[currentQuestionIndex.value].answer = nextAnswer;
        questions.value[currentQuestionIndex.value].llmResponse = payload.content;
        questions.value[currentQuestionIndex.value].offTopic = false;
    } catch (e) {
        logError(e);
        handleErrorFallback(new Error(t("onboarding.page.answerError")));
    } finally {
        isLoading.value = false;
        onFinish();
    }
}

function handleErrorFallback(e) {
    error.value = e;
    logError(e);
}

function postResponse(payload, { only = [], onFinish = () => {}, errorBag } = {}) {
    isLoading.value = true;

    router.post("", payload, {
        only,
        // Only need to load this once in the first request
        except: ["language_options"],
        errorBag,
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
        },
        onFinish: () => {
            isLoading.value = false;
            onFinish();
        },
    });
}

async function completeOnboarding(intent, { onFinish } = {}) {
    if (hasTeamInvitation) {
        if (isLoading.value) {
            return;
        }
        teamCompletionIntent = intent;
        teamCompletionFailed.value = false;
        isLoading.value = true;
        try {
            const result = await teamInviteOnboarding.complete(globalThis.location.pathname + globalThis.location.search, intent);
            teamCompletionFailed.value = result === "failed";
            if (result !== "not_invited") {
                return;
            }
        } catch (e) {
            teamCompletionFailed.value = true;
            logError(e);
            return;
        } finally {
            isLoading.value = false;
            onFinish?.();
        }
    }
    postResponse({ intent }, { only: intent === "done" ? ["step", "user_details"] : [], onFinish });
}

function goToTeam() {
    setTeamJoinDestination("team");
    void completeOnboarding("done");
}

function handleCheckin({ insight_checkin, onFinish }) {
    postResponse({ intent: "checkin", insight_checkin }, { only: ["step", "user_details", "insight_checkin", "post_onboarding_redirect"], onFinish });
}

function handleSkip() {
    showSkipDialog.value = false;
    void completeOnboarding("skip");
}

function onPersonalizationSubmit(formValues) {
    // Save language preference alongside personalization so it's persisted before the user moves on
    handleLanguagePreference();
    postResponse(
        { intent: "personalization", form: formValues, script_code: props.script_code },
        { only: ["step", "errors", "user_details", "role", "script_code", "greeting_flow"], errorBag: "personalization" },
    );
}

function getStarted() {
    if (hasTeamInvitation) {
        goToTeam();
        return;
    }
    postResponse({ intent: "get_started" });
}

function handlePathSelection(selectedPath) {
    setTeamJoinDestination(selectedPath === "chat" || selectedPath === "homepage" ? "current" : "team");
    postResponse(
        {
            intent: "path_selection",
            form: { onboarding_path_choice: selectedPath },
        },
        {
            only: ["step", "errors", "post_onboarding_redirect"],
        },
    );
}

function handleLanguagePreference() {
    postResponse({ intent: "language_preference", form: { default_language: userLanguage.value } }, { only: ["errors", "default_language"], errorBag: "language_preference" });
}
</script>

<style type="postcss">
@reference "../../../css/tailwind.css";

@layer components {
    .error-list li {
        @apply details text-chip-status-overdue-text text-left;
    }
}
</style>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity v-bind(FADE_DELAY_MS + "ms") ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    /* Message fade transition for cycling loading messages */
    .message-fade-enter-active,
    .message-fade-leave-active {
        transition: opacity 600ms ease;
    }

    .message-fade-enter-from,
    .message-fade-leave-to {
        opacity: 0;
    }

    .language-selector :deep(.language-highlight) {
        animation: language-pulse 1.5s ease-in-out 2;
        border-radius: 8px;
    }

    @keyframes language-pulse {
        0%,
        100% {
            background-color: transparent;
        }
        50% {
            background-color: color-mix(in srgb, var(--color-control-accent) 15%, transparent);
        }
    }

    .top-row {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        grid-template-rows: repeat(1, 1fr);
        gap: 20px;
    }

    .top-row-progress {
        grid-column: span 6 / span 6;
        grid-column-start: 4;
        grid-row-start: 1;
    }

    .top-row-skip {
        grid-column: span 3 / span 3;
        grid-column-start: 10;
        grid-row-start: 1;
    }
}
</style>
